import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FileZipOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Form, Popover, Row, Space, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { ERcbTier, EUserRole, ServiceTemplateField, TRANSLATABLE_SERVICE_TEMPLATE_ATTRIBUTES_LIST, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { Trans } from "@devowl-wp/react-frontend";
import { ServiceVariablesTip } from "./fields/service-variables-tip.js";
import { FormFieldServiceTemplateShouldUncheckContentBlockerCheckboxWhenOneOfList } from "./fields/should-uncheck-content-blocker-checkbox-when-one-of-list.js";
import { FormFieldServiceTemplateShouldUncheckContentBlockerCheckbox } from "./fields/should-uncheck-content-blocker-checkbox.js";
import { FormFieldServiceTemplateTagManagerOptInEventName } from "./fields/tagmanager-opt-in-event-name.js";
import { FormFieldServiceTemplateTagManagerOptOutEventName } from "./fields/tagmanager-opt-out-event-name.js";
import { FormFieldServiceTemplateTechnicalDefinitions } from "./fields/technical-definitions-form.js";
import { useFormEvent } from "../../../../../hooks/form/useFormEvent.js";
import { useServiceTemplateById } from "../../../../../hooks/service/useServiceTemplate.js";
import { useServiceTemplateMetaData } from "../../../../../hooks/service/useServiceTemplateMetaData.js";
import { useShouldVersionIncrease } from "../../../../../hooks/useShouldVersionIncrease.js";
import { useTranslation } from "../../../../../others/i18n.js";
import { useStores } from "../../../../../store/root.js";
import { colors } from "../../../../../styles/colors.js";
import { spacing, templateFormDefaultProps } from "../../../../../styles/spacing.js";
import { SORTER_ORDER_POSITION } from "../../../../../types/translation/translation.js";
import { areExtendedFieldsHidden, getInitialFormValueService, isExtendedFieldHidden, mapFromFormDynamicFieldsIntoServiceTemplate, mapFromFormPurposesIntoServiceTemplate, mapFromFormTechnicalDefinitionsIntoServiceTemplate, mapFromFormTranslationsIncludingProviderIntoServiceTemplate } from "../../../../../util/template/form.js";
import { clearFieldsByMergeStrategies } from "../../../../../util/template/merge-strategies.js";
import { getFieldNameTranslated } from "../../../../../util/template/translation.js";
import { mapSemverToVersion } from "../../../../../util/template/version.js";
import { ReactRouterPromptWrapper } from "../../../../third-party/react-router-prompt-wrapper.js";
import { RedirectModal } from "../../../redirect-modal.js";
import { PermissionCheck } from "../../user/permission.js";
import { UserTagLastEditors } from "../../user/user-tag-last-editors.js";
import { FormFieldTemplateDisplayName } from "../fields/display-name.js";
import { FormFieldTemplateEnabledWhenOneOfList } from "../fields/enabled-when-one-of-list.js";
import { FormFieldTemplateExtends } from "../fields/extends.js";
import { FormFieldTemplateHeadline } from "../fields/headline.js";
import { FormFieldTemplateIdentifier } from "../fields/identifier.js";
import { FormFieldTemplateIsDeleted } from "../fields/is-deleted.js";
import { FormFieldTemplateIsHidden } from "../fields/is-hidden.js";
import { FormFieldTemplateLogo } from "../fields/logo.js";
import { FormFieldTemplateMinRequiredRcbVersion, MIN_REQUIRED_CDN_VERSION } from "../fields/min-required-rcb-version.js";
import { FormFieldTemplateRecommendedWhenOneOfList } from "../fields/recommended-when-one-of-list.js";
import { TemplateMetaDataStatusTag } from "../fields/status.js";
import { FormFieldTemplateSubHeadline } from "../fields/sub-headline.js";
import { FormFieldTemplateSuccessorOfTemplate } from "../fields/successor-of-template.js";
import { FormFieldTemplateType } from "../fields/template-type.js";
import { FormFieldTemplateTier } from "../fields/tier.js";
import { FormFieldTemplateVersion } from "../fields/version.js";
import { FormFieldVisibilityWrapper, FormFieldVisibilityWrapperByCdn } from "../formFieldVisibilityWrapper.js";
import { ExportModal } from "../migration/export-dialog.js";
import { FormFieldServiceTemplateCodeOptIn } from "./fields/code-opt-in.js";
import { FormFieldServiceTemplateCodeOptOut } from "./fields/code-opt-out.js";
import { FormFieldServiceTemplateCodeOnPageLoad } from "./fields/codeOnPageLoad.js";
import { FormFieldServiceTemplateCreateContentBlockerNotice } from "./fields/create-content-blocker-notice.js";
import { FormFieldServiceTemplateDataProcessingInCountriesSpecialTreatment } from "./fields/data-processing-countries/data-processing-in-countries-special-treatment.js";
import { FormFieldServiceTemplateDataProcessingInCountries } from "./fields/data-processing-countries/data-processing-in-countries.js";
import { FormFieldServiceTemplateDynamicFields } from "./fields/dynamic-fields-form.js";
import { FormFieldServiceTemplateExecutePriority } from "./fields/execute-priority.js";
import { FormFieldServiceTemplateExtendsIdentifier } from "./fields/extends-Identifier.js";
import { FormFieldServiceTemplateGcmConsentTypes } from "./fields/gcm-consent-types.js";
import { FormFieldServiceTemplateGroupNotice } from "./fields/group-notice.js";
import { FormFieldServiceTemplateIsCdn } from "./fields/is-cdn.js";
import { FormFieldServiceTemplateIsEmbeddingOnlyExternalResources } from "./fields/is-embedding-only-external-resources.js";
import { FormFieldServiceTemplateIsExecuteCodeOptInWhenNoTagManagerConsentIsGiven } from "./fields/is-execute-code-opt-in-when-no-tagmanager-consent.js";
import { FormFieldServiceTemplateIsExecuteCodeOptOutWhenNoTagManagerConsentIsGiven } from "./fields/is-execute-code-opt-out-when-no-tagmanager-consent.js";
import { FormFieldServiceTemplateIsProviderCurrentSite } from "./fields/is-provider-current-site.js";
import { FormFieldServiceTemplateLegalBasisNotice } from "./fields/legal-basis-notice.js";
import { FormFieldServiceTemplateLegalBasis } from "./fields/legal-basis.js";
import { FormFieldServiceTemplateProviderLegalNoticeUrl } from "./fields/provider-legal-notice-url.js";
import { FormFieldServiceTemplateProviderNotice } from "./fields/provider-notice.js";
import { FormFieldServiceTemplateProviderPolicyUrl } from "./fields/provider-policy-url.js";
import { FormFieldServiceTemplateProvider } from "./fields/provider.js";
import { FormFieldServiceTemplatePurposes } from "./fields/purposes-form.js";
import { FormFieldServiceTemplateSccConclusionInstructionsNotice } from "./fields/scc-conclusion-instructions-notice.js";
import { FormFieldServiceTemplateServiceGroup } from "./fields/service-group.js";
import { FormFieldServiceTemplateTechnicalHandlingNotice } from "./fields/technical-handling-notice.js";
import { EFormEvent } from "../../../../../util/eventBus.js";
import { prepareExportDataService, prepareImportDataService } from "../../../../../util/export-import.js";
import { FormFieldTemplateLanguages } from "../fields/translation/language-form.js";
/**
 * Form for ServiceTemplate.
 */ const ServiceTemplateForm = observer(({ serviceTemplate, onSubmit, templateForm, isSubmitting = false, isLoading = false, isReadOnly = false, isModalView = false })=>{
    const { t } = useTranslation();
    const { rcbUserSettingsStore, notificationStore, rcbAuthenticationStore } = useStores();
    const { settings: { translationSettings } } = rcbUserSettingsStore;
    const [isPopOverOpen, setPopOverOpen] = useState(false);
    const [shouldVersionIncrease, toggleVersionIncrease] = useShouldVersionIncrease(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [extendedId, setExtendedId] = useState(serviceTemplate?.extends?.id);
    const useServiceTemplateExtended = useServiceTemplateById(extendedId);
    const { data: templateExtendedData } = useServiceTemplateExtended;
    useEffect(()=>{
        const { isFetched, isError } = useServiceTemplateExtended;
        if (isFetched && templateExtendedData) {
            const extendedTemplate = templateExtendedData;
            templateForm.setFieldValue(TemplateMetaDataField.Extends, extendedTemplate);
            extendedTemplate?.dynamicFields?.sort(SORTER_ORDER_POSITION);
            extendedTemplate?.purposes?.sort(SORTER_ORDER_POSITION);
            extendedTemplate?.technicalDefinitions?.sort(SORTER_ORDER_POSITION);
        } else if (isFetched && isError || extendedId === undefined) {
            templateForm.setFieldValue(TemplateMetaDataField.Extends, undefined);
        }
    }, [
        templateExtendedData,
        templateForm,
        extendedId
    ]);
    // on submitting form
    const submitServiceTemplate = useCallback(async (formTemplate)=>{
        const template = structuredClone(formTemplate);
        // Transform format from semver to our format
        template.minRequiredRcbVersion = mapSemverToVersion(template.minRequiredRcbVersion);
        if (template.isProviderCurrentWebsite === true) {
            // remove provider information, as it is not needed!
            delete template.provider;
        }
        // Transform Translations
        mapFromFormTranslationsIncludingProviderIntoServiceTemplate(template, templateForm);
        mapFromFormPurposesIntoServiceTemplate(template, templateForm);
        mapFromFormDynamicFieldsIntoServiceTemplate(template, templateForm);
        mapFromFormTechnicalDefinitionsIntoServiceTemplate(template, templateForm);
        const currentVersion = Number(templateForm.getFieldValue(TemplateMetaDataField.Version));
        if (shouldVersionIncrease && !isNaN(currentVersion)) {
            templateForm.setFieldValue(TemplateMetaDataField.Version, currentVersion + 1);
            template.version = templateForm.getFieldValue(TemplateMetaDataField.Version);
        }
        clearFieldsByMergeStrategies(template);
        if (template.group?.toString() === "") {
            template.group = null;
        }
        onSubmit?.(template, serviceTemplate);
    }, [
        shouldVersionIncrease,
        serviceTemplate
    ]);
    const onChangeExtendedTemplate = useCallback((template)=>{
        if (template && template.tier === ERcbTier.Pro) {
            templateForm.setFieldValue(TemplateMetaDataField.Tier, template.tier);
        }
        setExtendedId(template?.id);
    }, [
        templateForm,
        setExtendedId
    ]);
    const { Title } = Typography;
    // Options
    const yesOrNoOptions = [
        {
            label: t("Yes"),
            value: true
        },
        {
            label: t("No"),
            value: false
        }
    ];
    const initialValues = getInitialFormValueService(serviceTemplate, t);
    /** position: sticky; bottom: -25px; background: #f0f2f5; padding: 15px; */ const stickyButtonLayoutTop = useMemo(()=>{
        return {
            position: "sticky",
            top: isModalView ? "0px" : `-${spacing.layout.content.padding}px`,
            background: colors.main.background,
            zIndex: 2,
            paddingTop: `${spacing.layout.content.paddingSmall}px`,
            borderBottom: "1px solid #d3d3d342",
            flexFlow: "column",
            flex: "1"
        };
    }, []);
    const stickyButtonLayoutBottom = useMemo(()=>{
        return {
            position: "sticky",
            bottom: isModalView ? "0px" : `-${spacing.layout.content.padding}px`,
            background: colors.main.background,
            paddingTop: `${spacing.layout.content.padding}px`,
            paddingBottom: `${spacing.layout.content.paddingSmall}px`,
            zIndex: 1,
            borderTop: "1px solid #d3d3d342"
        };
    }, []);
    const [hiddenIdentifier, setHiddenIdentifier] = useState([]);
    useEffect(()=>{
        setHiddenIdentifier([
            serviceTemplate?.identifier
        ].filter(Boolean)); // TODO: refactor, use from form within FormFieldServiceTemplateExtendsIdentifier
        if (serviceTemplate) {
            templateForm.setFieldsValue(initialValues);
        }
    }, [
        serviceTemplate
    ]);
    const useServiceMetaData = useServiceTemplateMetaData(true, false, true, undefined, true);
    const { data: serviceMetaData } = useServiceMetaData;
    const [activeTemplates, setActiveTemplates] = useState([]);
    const [deletedTemplates, setDeletedTemplates] = useState([]);
    useEffect(()=>{
        setActiveTemplates(serviceMetaData?.filter((t)=>!t.isDeleted));
        setDeletedTemplates(serviceMetaData?.filter((t)=>t.isDeleted));
    }, [
        serviceMetaData
    ]);
    const formTitle = serviceTemplate?.id ? `${serviceTemplate.headline} ${serviceTemplate.subHeadline ? serviceTemplate.subHeadline : ""}` : t("Create Service Template");
    const [exportOpen, setExportOpen] = useState(false);
    const [exportData, setExportData] = useState(undefined);
    const [importData, setImportData] = useState(undefined);
    const [redirectOpen, setRedirectOpen] = useState(false);
    const [redirectToPath, setRedirectToPath] = useState();
    useEffect(()=>{
        if (importData) {
            const importable = JSON.parse(importData);
            const givenIdentifier = templateForm.getFieldValue(TemplateMetaDataField.Identifier);
            if (givenIdentifier && importable.identifier !== givenIdentifier) {
                notificationStore.add({
                    type: "warning",
                    message: t("Import canceled! The provided template identifier does not match the current template. Please ensure you are using a matching template."),
                    closable: true
                });
            } else if (!givenIdentifier && activeTemplates.findIndex((t)=>t.identifier === importable.identifier) >= 0) {
                setRedirectToPath(`/rcb/service/edit/${importable.identifier}`);
            } else {
                prepareImportDataService(importable);
                templateForm.setFieldsValue(importable);
                if (importable.extendsIdentifier) {
                    onChangeExtendedTemplate(activeTemplates.find((t)=>t.identifier === importable.extendsIdentifier));
                }
                notificationStore.add({
                    type: "success",
                    message: t("Import completed successfully!"),
                    duration: 5
                });
                setHasChanges(true);
                templateForm.validateFields();
            }
            setImportData(undefined);
        }
    }, [
        importData
    ]);
    useEffect(()=>{
        if (redirectToPath) {
            setRedirectOpen(true);
        }
    }, [
        redirectToPath
    ]);
    const translatorFieldPermissions = {
        whenRole: EUserRole.Translator,
        roleWhiteList: TRANSLATABLE_SERVICE_TEMPLATE_ATTRIBUTES_LIST,
        strategy: "any"
    };
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    useFormEvent(EFormEvent.TRANSLATION_FLAGS_CHANGED, ()=>{
        if (!hasChanges) {
            setHasChanges(true);
        }
    });
    return /*#__PURE__*/ _jsxs(Spin, {
        spinning: isLoading,
        children: [
            /*#__PURE__*/ _jsx(ReactRouterPromptWrapper, {
                when: hasChanges && !isSubmitting
            }),
            /*#__PURE__*/ _jsx(Helmet, {
                children: /*#__PURE__*/ _jsx("title", {
                    children: formTitle
                })
            }),
            /*#__PURE__*/ _jsxs(Row, {
                children: [
                    /*#__PURE__*/ _jsx(Col, {
                        style: {
                            flex: "1"
                        },
                        children: /*#__PURE__*/ _jsx(Title, {
                            level: 3,
                            style: {
                                paddingBottom: "0.2rem"
                            },
                            children: formTitle
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Col, {
                        children: [
                            /*#__PURE__*/ _jsx(PermissionCheck, {
                                minRole: EUserRole.Creator,
                                children: /*#__PURE__*/ _jsx(Button, {
                                    icon: /*#__PURE__*/ _jsx(FileZipOutlined, {}),
                                    type: "link",
                                    onClick: ()=>{
                                        const formData = prepareExportDataService(templateForm.getFieldsValue());
                                        setExportData(formData);
                                        setExportOpen(true);
                                    },
                                    children: t("Import/Export")
                                })
                            }),
                            /*#__PURE__*/ _jsx(ServiceVariablesTip, {}),
                            serviceTemplate?.id && /*#__PURE__*/ _jsx(TemplateMetaDataStatusTag, {
                                toolTip: `Id: ${serviceTemplate.id}`,
                                status: serviceTemplate.status
                            }),
                            /*#__PURE__*/ _jsx(UserTagLastEditors, {
                                lastKnownEditor: serviceTemplate?.lastEditedBy,
                                templateId: serviceTemplate?.id,
                                type: "Service",
                                disabled: !serviceTemplate?.lastEditedBy
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Row, {
                children: /*#__PURE__*/ _jsxs(Form, {
                    disabled: isLoading || isReadOnly,
                    name: "service-form",
                    form: templateForm,
                    initialValues: initialValues,
                    onFinish: (values)=>submitServiceTemplate(values),
                    ...templateFormDefaultProps,
                    onValuesChange: (changedValue, allValues)=>{
                        if (!hasChanges) {
                            setHasChanges(true); // only once
                        }
                    },
                    onFinishFailed: (event)=>{
                        for (const error of event.errorFields){
                            notificationStore.add({
                                type: "error",
                                message: error.errors.join(", "),
                                title: getFieldNameTranslated(String(error.name), t)
                            });
                        }
                    },
                    scrollToFirstError: true,
                    children: [
                        /*#__PURE__*/ _jsx(Row, {
                            style: translationSettings.isStickyMode ? stickyButtonLayoutTop : {
                                flexFlow: "column"
                            },
                            children: /*#__PURE__*/ _jsx(Row, {
                                style: {
                                    flexFlow: "inherit"
                                },
                                children: /*#__PURE__*/ _jsx(FormFieldTemplateLanguages, {})
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateType, {
                            type: "Service"
                        }),
                        /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
                            fieldNames: [
                                TemplateMetaDataField.Version,
                                TemplateMetaDataField.ExtendsIdentifier,
                                TemplateMetaDataField.Identifier,
                                TemplateMetaDataField.Logo,
                                TemplateMetaDataField.Headline,
                                TemplateMetaDataField.SubHeadline,
                                TemplateMetaDataField.SuccessorOfIdentifier
                            ],
                            permissionCheck: translatorFieldPermissions,
                            children: /*#__PURE__*/ _jsx(Title, {
                                level: 4,
                                children: t("Meta data")
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateVersion, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateExtends, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateExtendsIdentifier, {
                            onChangeExtendedTemplate: onChangeExtendedTemplate,
                            hiddenIdentifier: hiddenIdentifier,
                            templates: activeTemplates
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateIdentifier, {
                            enabled: !serviceTemplate?.id
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateLogo, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateHeadline, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateSubHeadline, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateSuccessorOfTemplate, {
                            activeTemplates: activeTemplates,
                            deletedTemplates: deletedTemplates,
                            hiddenIdentifier: hiddenIdentifier
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateIsDeleted, {}),
                        /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
                            fieldNames: [
                                ServiceTemplateField.IsCdn,
                                TemplateMetaDataField.Tier,
                                TemplateMetaDataField.MinRequiredRcbVersion,
                                TemplateMetaDataField.Identifier,
                                TemplateMetaDataField.EnabledWhenOneOf,
                                TemplateMetaDataField.RecommendedWhenOneOf,
                                TemplateMetaDataField.IsHidden
                            ],
                            permissionCheck: translatorFieldPermissions,
                            children: /*#__PURE__*/ _jsx(Title, {
                                level: 4,
                                children: t("Conditions")
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateTier, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateMinRequiredRcbVersion, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateEnabledWhenOneOfList, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateRecommendedWhenOneOfList, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateIsHidden, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateIsCdn, {
                            options: yesOrNoOptions,
                            onIsCdnChange: (value)=>{
                                if (value) {
                                    templateForm.setFieldValue(TemplateMetaDataField.Tier, ERcbTier.Free);
                                    if (templateForm.getFieldValue(TemplateMetaDataField.MinRequiredRcbVersion) === "0.0.0") {
                                        templateForm.setFieldValue(TemplateMetaDataField.MinRequiredRcbVersion, MIN_REQUIRED_CDN_VERSION);
                                    }
                                    templateForm.setFieldValue(TemplateMetaDataField.IsHidden, false);
                                    notificationStore.add({
                                        type: "info",
                                        message: t("Service templates for CDNs must always be available in the free product tier. It must also be described which SCCs can be concluded."),
                                        duration: 10,
                                        closable: true
                                    });
                                }
                            }
                        }),
                        /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
                            fieldNames: [
                                TemplateMetaDataField.Name,
                                ServiceTemplateField.Group,
                                ServiceTemplateField.GroupNotice,
                                ServiceTemplateField.IsProviderCurrentWebsite,
                                ServiceTemplateField.ProviderNotice,
                                ServiceTemplateField.Provider,
                                ServiceTemplateField.ProviderPrivacyPolicyUrl,
                                ServiceTemplateField.ProviderLegalNoticeUrl,
                                ServiceTemplateField.Purposes,
                                ServiceTemplateField.LegalBasisNotice,
                                ServiceTemplateField.LegalBasis,
                                ServiceTemplateField.DataProcessingInCountries,
                                ServiceTemplateField.DataProcessingInCountriesSpecialTreatments
                            ],
                            permissionCheck: translatorFieldPermissions,
                            forceHiddenFormCallback: areExtendedFieldsHidden([
                                TemplateMetaDataField.Name,
                                ServiceTemplateField.GroupNotice,
                                ServiceTemplateField.ProviderNotice,
                                ServiceTemplateField.Provider,
                                ServiceTemplateField.ProviderPrivacyPolicyUrl,
                                ServiceTemplateField.ProviderLegalNoticeUrl,
                                ServiceTemplateField.Purposes,
                                ServiceTemplateField.LegalBasisNotice
                            ], hasCreatorRole),
                            children: /*#__PURE__*/ _jsx(Title, {
                                level: 4,
                                children: t("General")
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateDisplayName, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateServiceGroup, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateGroupNotice, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateIsProviderCurrentSite, {
                            options: yesOrNoOptions
                        }),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateProviderNotice, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateProvider, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateProviderPolicyUrl, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateProviderLegalNoticeUrl, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplatePurposes, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateLegalBasisNotice, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateLegalBasis, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateDataProcessingInCountries, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateDataProcessingInCountriesSpecialTreatment, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateSccConclusionInstructionsNotice, {}),
                        /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
                            fieldNames: [
                                ServiceTemplateField.IsEmbeddingOnlyExternalResources,
                                ServiceTemplateField.TechnicalDefinitions
                            ],
                            forceHidden: !hasCreatorRole && templateForm.getFieldValue(ServiceTemplateField.IsEmbeddingOnlyExternalResources) === true,
                            forceHiddenFormCallback: isExtendedFieldHidden(ServiceTemplateField.TechnicalDefinitions, hasCreatorRole),
                            children: /*#__PURE__*/ _jsx(Title, {
                                level: 4,
                                children: t("Technical cookie information")
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateIsEmbeddingOnlyExternalResources, {
                            options: yesOrNoOptions
                        }),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateTechnicalDefinitions, {
                            form: templateForm
                        }),
                        /*#__PURE__*/ _jsxs(FormFieldVisibilityWrapperByCdn, {
                            children: [
                                /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
                                    fieldNames: [
                                        ServiceTemplateField.TechnicalHandlingNotice,
                                        ServiceTemplateField.TagManagerOptInEventName,
                                        ServiceTemplateField.TagManagerOptOutEventName,
                                        ServiceTemplateField.DynamicFields,
                                        ServiceTemplateField.CodeOptIn,
                                        ServiceTemplateField.ExecuteCodeOptInWhenNoTagManagerConsentIsGiven,
                                        ServiceTemplateField.CodeOptOut,
                                        ServiceTemplateField.ExecuteCodeOptOutWhenNoTagManagerConsentIsGiven,
                                        ServiceTemplateField.CodeOnPageLoad,
                                        ServiceTemplateField.GoogleConsentModeConsentTypes
                                    ],
                                    permissionCheck: translatorFieldPermissions,
                                    forceHiddenFormCallback: areExtendedFieldsHidden([
                                        ServiceTemplateField.TechnicalHandlingNotice,
                                        ServiceTemplateField.DynamicFields
                                    ], hasCreatorRole),
                                    children: /*#__PURE__*/ _jsxs(Row, {
                                        justify: "space-between",
                                        align: "middle",
                                        children: [
                                            /*#__PURE__*/ _jsx(Col, {
                                                children: /*#__PURE__*/ _jsx(Title, {
                                                    level: 4,
                                                    children: t("Technical handling")
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Col, {
                                                style: {
                                                    textAlign: "right"
                                                },
                                                children: /*#__PURE__*/ _jsx(FormFieldServiceTemplateExecutePriority, {})
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(FormFieldServiceTemplateTechnicalHandlingNotice, {}),
                                /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
                                    fieldNames: [
                                        ServiceTemplateField.TagManagerOptInEventName,
                                        ServiceTemplateField.TagManagerOptOutEventName
                                    ],
                                    permissionCheck: translatorFieldPermissions,
                                    children: /*#__PURE__*/ _jsxs(Form.Item, {
                                        label: /*#__PURE__*/ _jsx(Trans, {
                                            children: "Event names for Google Tag Manager"
                                        }),
                                        children: [
                                            /*#__PURE__*/ _jsx(FormFieldServiceTemplateTagManagerOptInEventName, {}),
                                            /*#__PURE__*/ _jsx(FormFieldServiceTemplateTagManagerOptOutEventName, {})
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(FormFieldServiceTemplateDynamicFields, {}),
                                /*#__PURE__*/ _jsx(FormFieldServiceTemplateGcmConsentTypes, {}),
                                /*#__PURE__*/ _jsx(FormFieldServiceTemplateCodeOptIn, {}),
                                /*#__PURE__*/ _jsx(FormFieldServiceTemplateIsExecuteCodeOptInWhenNoTagManagerConsentIsGiven, {}),
                                /*#__PURE__*/ _jsx(FormFieldServiceTemplateCodeOptOut, {}),
                                /*#__PURE__*/ _jsx(FormFieldServiceTemplateIsExecuteCodeOptOutWhenNoTagManagerConsentIsGiven, {}),
                                /*#__PURE__*/ _jsx(FormFieldServiceTemplateCodeOnPageLoad, {})
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(FormFieldVisibilityWrapperByCdn, {
                            children: [
                                /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
                                    fieldNames: [
                                        ServiceTemplateField.ShouldUncheckContentBlockerCheckbox,
                                        ServiceTemplateField.ShouldUncheckContentBlockerCheckboxWhenOneOf,
                                        ServiceTemplateField.CreateContentBlockerNotice
                                    ],
                                    permissionCheck: translatorFieldPermissions,
                                    forceHiddenFormCallback: isExtendedFieldHidden(ServiceTemplateField.CreateContentBlockerNotice, hasCreatorRole),
                                    children: /*#__PURE__*/ _jsx(Title, {
                                        level: 4,
                                        children: t("Content Blocker")
                                    })
                                }),
                                /*#__PURE__*/ _jsx(FormFieldServiceTemplateShouldUncheckContentBlockerCheckbox, {
                                    options: yesOrNoOptions
                                }),
                                /*#__PURE__*/ _jsx(FormFieldServiceTemplateShouldUncheckContentBlockerCheckboxWhenOneOfList, {}),
                                /*#__PURE__*/ _jsx(FormFieldServiceTemplateCreateContentBlockerNotice, {})
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Row, {
                style: stickyButtonLayoutBottom,
                justify: "center",
                align: "middle",
                children: serviceTemplate?.id ? /*#__PURE__*/ _jsx(Popover, {
                    trigger: "click",
                    placement: "top",
                    title: t("Save Template"),
                    open: isPopOverOpen,
                    onOpenChange: (newOpenState)=>setPopOverOpen(newOpenState),
                    content: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(PermissionCheck, {
                                minRole: EUserRole.Creator,
                                children: [
                                    /*#__PURE__*/ _jsx(Row, {
                                        children: /*#__PURE__*/ _jsx(Checkbox, {
                                            checked: shouldVersionIncrease,
                                            onChange: toggleVersionIncrease,
                                            children: t("Notify users")
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Row, {
                                        children: /*#__PURE__*/ _jsx("br", {})
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Row, {
                                children: /*#__PURE__*/ _jsxs(Space, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Button, {
                                            onClick: ()=>setPopOverOpen(false),
                                            children: t("Cancel")
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            disabled: !shouldVersionIncrease && !hasChanges,
                                            style: {
                                                width: "10rem"
                                            },
                                            type: "primary",
                                            onClick: ()=>{
                                                templateForm.submit();
                                                setPopOverOpen(false);
                                            },
                                            loading: isLoading,
                                            children: shouldVersionIncrease ? t("Save & Notify") : t("Save")
                                        })
                                    ]
                                })
                            })
                        ]
                    }),
                    children: /*#__PURE__*/ _jsx(Button, {
                        type: "primary",
                        children: t("Save")
                    })
                }) : /*#__PURE__*/ _jsx(Button, {
                    type: "primary",
                    onClick: ()=>templateForm.submit(),
                    loading: isLoading,
                    children: t("Create")
                })
            }),
            exportOpen ? /*#__PURE__*/ _jsx(ExportModal, {
                open: exportOpen,
                onCancel: ()=>{
                    setExportOpen(false);
                },
                exportableData: exportData,
                onSubmit: (importData)=>{
                    setExportOpen(false);
                    setImportData(importData);
                }
            }) : null,
            /*#__PURE__*/ _jsx(RedirectModal, {
                redirectToPath: redirectToPath,
                open: redirectOpen,
                onCancel: ()=>{
                    setRedirectOpen(false);
                    setRedirectToPath(undefined);
                },
                title: t("Service already exists"),
                okText: t("Redirect"),
                children: t("Import canceled. The template to import already exists. Would you like to be taken to it?")
            })
        ]
    });
});
export { ServiceTemplateForm };
